import type { CartonAlias } from '@skupreme/api-contracts/core';
import { DotsThreeIcon, InfoIcon } from '@phosphor-icons/react';
import {
  Button,
  Cell,
  Image,
  Menu,
  Row,
  RowExpandButton,
  TextField,
  Tooltip,
} from '@skupreme/shared/ui';
import { ItemIdentifier } from '@features/catalog';
import { CommonUtility } from '@utility/common';
import { CatalogHelpText } from '@utility/constants/strings';
import { useState } from 'react';
import { Link } from 'react-router';
import { type CartonRow, mainAlias } from '../lib/rows';
import { CartonSummary } from './CartonSummary';
import { SupplierBadges } from './SupplierBadges';

export const CARTON_COLUMN_COUNT = 7;

export type CartonRowContext = {
  imperial: boolean;
  readOnly?: boolean;
  /** The page's permission: actions show disabled, or not at all. */
  actionsDisabled?: boolean;
  actionsHidden?: boolean;
  entity: (row: CartonRow) => string;
  onEntityChange: (row: CartonRow, entity: string) => void;
  /** Without an alias, adds a new one. */
  onEditAlias: (row: CartonRow, alias?: string) => void;
  onDeleteAlias: (row: CartonRow, alias: string) => void;
};

type Action = { id: string; label: string; isDisabled?: boolean };

function RowActions({
  label,
  actions,
  onAction,
}: {
  label: string;
  actions: Action[];
  onAction: (id: string) => void;
}) {
  if (!actions.length) return null;

  return (
    <Menu
      aria-label={label}
      trigger={
        <Button
          variant="ghost"
          shape="circle"
          aria-label={label}
          className="sk:size-6 sk:opacity-0 sk:group-hover:opacity-100 sk:focus-visible:opacity-100 sk:aria-expanded:opacity-100"
        >
          <DotsThreeIcon size={16} weight="bold" />
        </Button>
      }
      actions={actions}
      onAction={onAction}
    />
  );
}

function Product({ row }: { row: CartonRow }) {
  return (
    <div className="sk:flex sk:items-center sk:gap-3">
      <Image src={row.image} alt="" size="md" fit="contain" />
      <div className="sk:flex sk:min-w-0 sk:flex-col sk:gap-1">
        <span className="sk:text-sm sk:text-fg">{row.title}</span>
        {row.sku ? (
          row.skuLink ? (
            <span className="sk:flex sk:min-w-0 sk:items-center sk:gap-1 sk:text-xs">
              <span className="sk:text-fg-tertiary">SKU</span>
              <Link
                to={row.skuLink}
                className="sk:truncate sk:text-fg-secondary sk:no-underline sk:hover:underline"
              >
                {row.sku}
              </Link>
            </span>
          ) : (
            <ItemIdentifier label="SKU" value={row.sku} />
          )
        ) : null}
        {row.identifier ? (
          <ItemIdentifier
            label={row.identifier.label}
            value={row.identifier.value}
          />
        ) : null}
        <SupplierBadges suppliers={row.suppliers} />
      </div>
    </div>
  );
}

const amount = (value?: number) =>
  value === undefined ? '—' : CommonUtility.numberWithCommas(value);

/** One alias of a row with several, under it while it is open. */
function AliasRow({
  row,
  alias,
  context,
}: {
  row: CartonRow;
  alias: CartonAlias;
  context: CartonRowContext;
}) {
  const locked = !!context.readOnly || !!context.actionsDisabled;
  const actions: Action[] = context.actionsHidden
    ? []
    : [
        { id: 'edit', label: 'Edit carton', isDisabled: locked },
        ...(alias.primary
          ? []
          : [{ id: 'delete', label: 'Delete alias', isDisabled: locked }]),
      ];

  return (
    <Row id={`${row.id}/${alias.alias}`} isNested>
      <Cell>
        <div className="sk:flex sk:min-w-0 sk:flex-col sk:gap-0.5">
          <span className="sk:inline-flex sk:items-center sk:gap-1 sk:text-xs sk:text-fg-tertiary">
            {alias.primary ? 'Primary carton alias' : 'Carton alias'}
            <Tooltip content={CatalogHelpText.carton}>
              <InfoIcon size={12} aria-label="What is a carton alias" />
            </Tooltip>
          </span>
          <ItemIdentifier label="Alias" value={alias.alias} />
        </div>
      </Cell>
      <Cell />
      <Cell />
      <Cell />
      <Cell />
      <Cell>
        <CartonSummary alias={alias} imperial={context.imperial} />
      </Cell>
      <Cell className="sk:text-right">
        <RowActions
          label={`Actions for alias ${alias.alias}`}
          actions={actions}
          onAction={id =>
            id === 'edit'
              ? context.onEditAlias(row, alias.alias)
              : context.onDeleteAlias(row, alias.alias)
          }
        />
      </Cell>
    </Row>
  );
}

/** An item or component, and while it is open, each of its carton aliases. */
export function CartonRows({
  row,
  context,
}: {
  row: CartonRow;
  context: CartonRowContext;
}) {
  const [isExpanded, setExpanded] = useState(false);
  const isMultiple = row.aliases.length > 1;
  const alias = mainAlias(row);

  const actions: Action[] = context.readOnly
    ? []
    : [
        ...(isMultiple || context.actionsHidden
          ? []
          : [
              {
                id: 'edit',
                label: 'Edit carton',
                isDisabled: context.actionsDisabled,
              },
            ]),
        { id: 'add', label: 'Add alias' },
      ];

  return (
    <>
      <Row id={row.id}>
        <Cell>
          <Product row={row} />
        </Cell>
        <Cell className="sk:tabular-nums">
          {row.price === undefined
            ? '—'
            : CommonUtility.currencyFormat(row.price)}
        </Cell>
        <Cell className="sk:tabular-nums">{amount(row.leadTime)}</Cell>
        <Cell className="sk:tabular-nums">{amount(row.moq)}</Cell>
        <Cell>
          <TextField
            size="sm"
            label={`Entity of ${row.sku || row.title}`}
            placeholder="Entity"
            value={context.entity(row)}
            onChange={value => context.onEntityChange(row, value)}
            isDisabled={!!context.readOnly}
          />
        </Cell>
        <Cell>
          {isMultiple ? (
            <span className="sk:inline-flex sk:items-center sk:gap-1 sk:text-xs sk:text-fg-secondary">
              {row.aliases.length} carton aliases
              <RowExpandButton
                isExpanded={isExpanded}
                onExpandedChange={setExpanded}
                aria-label={`${isExpanded ? 'Hide' : 'Show'} carton aliases of ${row.sku || row.title}`}
              />
            </span>
          ) : (
            <CartonSummary alias={alias} imperial={context.imperial} />
          )}
        </Cell>
        <Cell className="sk:text-right">
          <RowActions
            label={`Carton actions for ${row.sku || row.title}`}
            actions={actions}
            onAction={id =>
              context.onEditAlias(row, id === 'edit' ? alias?.alias : undefined)
            }
          />
        </Cell>
      </Row>
      {isMultiple && isExpanded
        ? row.aliases.map(item => (
            <AliasRow
              key={item._id ?? item.alias}
              row={row}
              alias={item}
              context={context}
            />
          ))
        : null}
    </>
  );
}
